/**
 * Service de présence — côté msd_admin.
 * Deux usages :
 *   1. ÉCRITURE : présence du STAFF (même mécanisme hybride que le portail) — non affichée en v1
 *      mais préparée pour le futur « conseiller en ligne » côté tableau de bord client.
 *   2. LECTURE : abonnement temps réel à `presence/{clientUid}` pour l'affichage admin
 *      (pastille « en ligne »). La lecture est autorisée par les règles via isAdmin().
 *
 * Modèle : `presence/{uid}` = { online, role: 'client'|'staff', lastSeen, startedAt }.
 * L'affichage « hors ligne » est déduit de la fraîcheur de lastSeen (crash/fermeture non détectée).
 */

import { doc, setDoc, getDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { firestore } from './firebase/firebase';

// Au-delà de cette durée sans écriture, l'utilisateur est considéré « hors ligne ».
export const OFFLINE_AFTER_MS = 5 * 60 * 1000;

const TICK_INTERVAL_MS = 2 * 60 * 1000; // tick sécurité (uniquement onglet visible)
const HIDDEN_OFFLINE_DELAY_MS = 45 * 1000;

let presenceUid = null;
let presenceRole = 'staff';
let tickTimer = null;
let hiddenTimer = null;

// ---------------------------------------------------------------------------
// ÉCRITURE (staff / futur affichage côté client)
// ---------------------------------------------------------------------------

function writePresence(payload) {
  if (!presenceUid) return Promise.resolve();
  return setDoc(
    doc(firestore, 'presence', presenceUid),
    {
      uid: presenceUid,
      role: presenceRole,
      ...payload,
      lastSeen: serverTimestamp(),
    },
    { merge: true }
  ).catch((error) => {
    console.warn('[presence] écriture impossible :', error);
  });
}

const markOnline = () => writePresence({ online: true });
const markOffline = () => writePresence({ online: false });

function clearTimers() {
  if (tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
  }
  if (hiddenTimer) {
    clearTimeout(hiddenTimer);
    hiddenTimer = null;
  }
}

function handleVisibilityChange() {
  if (document.hidden) {
    if (presenceUid && !hiddenTimer) {
      hiddenTimer = setTimeout(() => {
        hiddenTimer = null;
        markOffline();
      }, HIDDEN_OFFLINE_DELAY_MS);
    }
  } else {
    if (hiddenTimer) {
      clearTimeout(hiddenTimer);
      hiddenTimer = null;
    }
    markOnline();
  }
}

function handlePageHide() {
  clearTimers();
  if (presenceUid) {
    markOffline();
  }
}

/**
 * Démarre la présence du staff (app admin authentifiée).
 * @param {string} uid - uid Firebase de l'admin
 * @param {{ role?: 'staff'|'client' }} [options]
 */
export async function startPresence(uid, options = {}) {
  if (!uid) return;
  stopPresence();
  presenceUid = uid;
  presenceRole = options.role || 'staff';
  markOnline();

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);

  tickTimer = setInterval(() => {
    if (!document.hidden && document.visibilityState === 'visible') {
      markOnline();
    }
  }, TICK_INTERVAL_MS);
}

/** Arrête la présence du staff (logout). */
export function stopPresence() {
  clearTimers();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('pagehide', handlePageHide);
  if (presenceUid) {
    markOffline();
  }
  presenceUid = null;
}

// ---------------------------------------------------------------------------
// LECTURE (affichage admin de la présence d'un client)
// ---------------------------------------------------------------------------

/**
 * Récupère le rôle d'un uid depuis son document utilisateur (lecture directe admin).
 * @param {string} uid
 * @returns {Promise<'staff'|'client'|null>}
 */
export async function getPresenceRole(uid) {
  try {
    const snapshot = await getDoc(doc(firestore, 'users', uid));
    if (!snapshot.exists()) return null;
    return snapshot.data().isAdmin === true ? 'staff' : 'client';
  } catch (error) {
    console.warn('[presence] lecture rôle impossible :', error);
    return null;
  }
}

/**
 * S'abonne aux changements de présence d'un uid.
 * @param {string} uid - uid ciblé
 * @param {(data: Object|null) => void} onChange - appelé avec le contenu du doc (ou null)
 * @returns {() => void} fonction d'abandon
 */
export function subscribePresence(uid, onChange) {
  if (!uid) {
    onChange(null);
    return () => {};
  }
  return onSnapshot(
    doc(firestore, 'presence', uid),
    (snapshot) => {
      onChange(snapshot.exists() ? snapshot.data() : null);
    },
    (error) => {
      console.warn('[presence] abonnement en erreur :', error);
      onChange(null);
    }
  );
}

/**
 * Calcule l'état de présence à partir des données du doc.
 * @param {Object|null} data - contenu du doc presence/{uid}
 * @returns {'online'|'offline'|'unknown'}
 */
export function getPresenceState(data) {
  if (!data) return 'unknown';
  if (data.online === false) return 'offline';

  let lastSeenMs = null;
  if (data.lastSeen) {
    if (typeof data.lastSeen.toMillis === 'function') lastSeenMs = data.lastSeen.toMillis();
    else if (data.lastSeen.seconds) lastSeenMs = data.lastSeen.seconds * 1000;
  }

  // Doc périmé (aucune écriture depuis OFFLINE_AFTER_MS) → considéré hors ligne,
  // même si `online` est resté true après un crash / une fermeture non détectée.
  if (lastSeenMs !== null && Date.now() - lastSeenMs >= OFFLINE_AFTER_MS) return 'offline';
  if (data.online === true) return 'online';
  return 'unknown';
}